import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { TopNav } from "../components/nav";
import { Footer } from "../components/footer";
import { I18nProvider, useI18n } from "../lib/i18n";
import { Toaster } from "../components/ui/sonner";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Seite nicht gefunden</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Die gesuchte Seite existiert nicht oder wurde verschoben.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-indigo px-4 py-2 text-sm font-medium text-indigo-foreground transition-colors hover:opacity-90"
          >
            Zur Startseite
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Diese Seite konnte nicht geladen werden
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Bitte versuchen Sie es erneut oder kehren Sie zur Startseite zurück.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-indigo px-4 py-2 text-sm font-medium text-indigo-foreground transition-colors hover:opacity-90"
          >
            Erneut versuchen
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Zur Startseite
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "theme-color", content: "#0f172a" },
      { title: "Retatrutid Thailand – Retatrutide & Weight Loss Therapies" },
      {
        name: "description",
        content:
          "Modern weight management with Reta. Learn everything about the future of Retatrutide and current, legal peptide therapies.",
      },
      { name: "google-site-verification", content: "UqWf_dxh9x-GbfbPmvkICTwufGF51tRWhVLxe9cOq9I" },
      { name: "keywords", content: "Retatrutid, Thailand, Retatrutid Thailand, Retatrutide, LY3437943, GLP-1, Triple-Agonist, TRIUMPH, Semaglutid, Tirzepatid, Wegovy, Mounjaro, Ozempic, Bangkok, klinische Studie" },
      { name: "author", content: "Retatrutid Thailand" },
      { property: "og:site_name", content: "Retatrutid Thailand" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      // Geo-Targeting Thailand – Zielmarkt: Expats in TH.
      { name: "geo.region", content: "TH" },
      { name: "geo.placename", content: "Bangkok, Thailand" },
      { name: "geo.position", content: "13.7563;100.5018" },
      { name: "ICBM", content: "13.7563, 100.5018" },
      { name: "target_country", content: "TH" },
      { name: "distribution", content: "global" },
      { name: "coverage", content: "Thailand" },
      { name: "audience", content: "Expats in Thailand" },
      { property: "og:title", content: "Retatrutid Thailand – Retatrutide & Weight Loss Therapies" },
      { name: "twitter:title", content: "Retatrutid Thailand – Retatrutide & Weight Loss Therapies" },
      { property: "og:description", content: "Modern weight management with Reta. Learn everything about the future of Retatrutide and current, legal peptide therapies." },
      { name: "twitter:description", content: "Modern weight management with Reta. Learn everything about the future of Retatrutide and current, legal peptide therapies." },
      { property: "og:image", content: "https://retatrutide-thailand.org/__l5e/assets-v1/992278f5-0d20-4164-bc97-8fba1d4e6200/retatrutid-thailand-og.jpg" },
      { property: "og:image:secure_url", content: "https://retatrutide-thailand.org/__l5e/assets-v1/992278f5-0d20-4164-bc97-8fba1d4e6200/retatrutid-thailand-og.jpg" },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Retatrutid Thailand – ärztlich begleitete Behandlung in Bangkok" },
      { name: "twitter:image", content: "https://retatrutide-thailand.org/__l5e/assets-v1/992278f5-0d20-4164-bc97-8fba1d4e6200/retatrutid-thailand-og.jpg" },
      { name: "twitter:image:alt", content: "Retatrutid Thailand – ärztlich begleitete Behandlung in Bangkok" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/favicon.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "stylesheet", href: "/fonts/inter.css" },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/UcC73FwrK3iLTeHuS_nV.woff2",
        crossOrigin: "anonymous",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://retatrutide-thailand.org/#organization",
          name: "Retatrutid Thailand",
          url: "https://retatrutide-thailand.org",
          logo: {
            "@type": "ImageObject",
            url: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/bb6a2547-8be4-41a6-91b6-6a1549310a91",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://retatrutide-thailand.org/#website",
          name: "Retatrutid Thailand",
          url: "https://retatrutide-thailand.org",
          publisher: { "@id": "https://retatrutide-thailand.org/#organization" },
          description:
            "Retatrutid Thailand – wissenschaftliches Informationsportal zum Wirkstoff Retatrutid (Triple-Agonist) mit Fokus auf Expats in Thailand.",
          inLanguage: ["de", "en"],
          audience: {
            "@type": "Audience",
            audienceType: "Expats and foreign residents in Thailand",
            geographicArea: {
              "@type": "Country",
              name: "Thailand",
              identifier: "TH",
            },
          },
        }),
      },
    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <script
          dangerouslySetInnerHTML={{
            __html: `try{var e=document.documentElement,t=localStorage.getItem('theme'),d=t?t==='dark':matchMedia('(prefers-color-scheme:dark)').matches,l=new URLSearchParams(location.search).get('lang')||localStorage.getItem('lang')||(navigator.language||'en').toLowerCase().slice(0,2),c=e.className;d&&(c+=' dark');e.className=c;e.lang=l==='de'?'de':'en';}catch(e){}`,
          }}
        />
        {children}
        <Scripts />
      </body>
    </html>
  );
}


function LocalizedNotFound() {
  const { t } = useI18n();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">{t.notFound.title}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t.notFound.body}</p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-indigo px-4 py-2 text-sm font-medium text-indigo-foreground transition-colors hover:opacity-90"
          >
            {t.notFound.home}
          </Link>
        </div>
      </div>
    </div>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <div className="min-h-screen">
          <TopNav />
          <main>
            <Outlet />
          </main>
          <Footer />
        </div>
        <Toaster />
      </I18nProvider>
    </QueryClientProvider>
  );
}

